import { Modal } from "bootstrap";
import React, { createContext, ReactNode, useEffect, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { useBetModalPageLock } from "../hooks/useBetModalPageLock.ts";
import { lockPageScroll } from "../helpers/scrollPageToTop.ts";

interface ModalProviderProps {
    children: ReactNode;
}

const ModalProvider = ({ children }: ModalProviderProps) => {

    const [modalContent, setModalContent] = useState<ReactNode>(null);
    const [modalInstance, setModalInstance] = useState<Modal | null>(null);
    const modalInstanceRef = useRef<Modal | null>(null);
    const modalId = 'jocksportsModal';
    useBetModalPageLock(modalId);

    useEffect(() => {
        const modalElement = document.getElementById(modalId);
        if (!modalElement) return;

        const instance = new Modal(modalElement, { focus: false });
        // On the mobile sheet, Bootstrap's overflow:hidden zeros iOS scroll.
        // Desktop still needs the real scrollbar helper so the overlay stays centered.
        const scrollBar = (instance as unknown as { _scrollBar?: { hide: () => void; reset: () => void } })._scrollBar;
        if (scrollBar) {
            const originalHide = scrollBar.hide.bind(scrollBar);
            const originalReset = scrollBar.reset.bind(scrollBar);
            const isMobileSheet = () => window.matchMedia("(max-width: 960px)").matches;
            scrollBar.hide = () => {
                if (isMobileSheet()) return;
                originalHide();
            };
            scrollBar.reset = () => {
                if (isMobileSheet()) return;
                originalReset();
            };
        }
        modalInstanceRef.current = instance;
        setModalInstance(instance);

        // Unmount modal body on close so ParlayBets remounts and its effects re-run on next open
        const onHidden = () => setModalContent(null);
        modalElement.addEventListener('hidden.bs.modal', onHidden);

        return () => {
            modalElement.removeEventListener('hidden.bs.modal', onHidden);
        };
    }, []);

    const showModal = (content?: ReactNode) => {
        lockPageScroll();
        if (content) {
            flushSync(() => setModalContent(content));
        }
        (modalInstanceRef.current ?? modalInstance)?.show();
    };

    const closeModal = () => {
        (modalInstanceRef.current ?? modalInstance)?.hide();
    };

    const updateModalContent = (content: ReactNode) => {
        setModalContent(content);
    };

    const data = {
        showModal,
        closeModal,
        updateModalContent
    };

    const modal = (
        <div className="modal fade modal-bottom-small is-bet-modal is-parlay-modal" id="jocksportsModal" tabIndex={-1}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable modal-fullscreen-lg-down">
                <div className="modal-content content-modal modal-extens-responsive ">
                    <div className="bet-modal-body">
                        {modalContent}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <ModalContext.Provider value={data}>
            {children}
            {createPortal(modal, document.body)}
        </ModalContext.Provider>
    );
};

export { ModalProvider };

interface ModalContextType {
    showModal: (content?: ReactNode) => void;
    closeModal: () => void;
    updateModalContent: (content: ReactNode) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
    const context = React.useContext(ModalContext);
    if (!context) {
        throw new Error("useModal must be used within ModalProvider");
    }
    return context;
};
export default ModalContext;
